/**
 * What is left of `useAction` once the store does the rest.
 *
 * `useAction` did three things: it ran a write, it refetched, and it caught
 * the error every Supabase call returns so a failed write and a slow one could
 * not look identical. The first two belong to the mutations in `mutations.ts`
 * now — the write is the `mutationFn`, and there is nothing to refetch because
 * `onMutate` already patched the cache.
 *
 * The third is still needed and is still nobody else's job: a screen shows one
 * `.writeerr` line, and it has to say whatever the last write said. Nothing
 * here swallows anything, which was the original file's whole point.
 */

/** The parts of a `UseMutationResult` this needs. Structural rather than the
 *  imported type, so a screen can pass mutations with unrelated variables and
 *  results without four type arguments at every call site. */
export interface WriteResult {
  status: 'idle' | 'pending' | 'error' | 'success'
  submittedAt: number
  error: { message: string } | null
}

/**
 * The message from the most recent write, or nothing.
 *
 * Most recent rather than first-found, because that is what `useAction`'s
 * `setError(null)` at the top of every run actually meant: an error stays on
 * screen until something else is tried, and then it is that attempt's answer
 * you are reading. A screen with six mutations on it would otherwise go on
 * showing February's failure under March's successful save.
 */
export function useWriteError(...results: WriteResult[]): string | null {
  let latest: WriteResult | null = null
  for (const r of results) {
    if (r.status === 'idle') continue
    if (!latest || r.submittedAt > latest.submittedAt) latest = r
  }
  return latest?.error?.message ?? null
}

/**
 * Did it work?
 *
 * `mutateAsync` rejects on failure, and several callers here need the boolean
 * `useAction`'s `run` returned — "close the sheet only if the write landed".
 * The rejection is already recorded on the mutation, which is where
 * `useWriteError` reads it from, so swallowing it here loses nothing.
 */
export async function attempt(work: Promise<unknown>): Promise<boolean> {
  try {
    await work
    return true
  } catch {
    return false
  }
}
